import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, InputNumber, Modal, message } from 'antd';
import { Box, Text } from 'atoms';
import { numberWithCommas } from 'utils/NumberCommaSeprator';

const { TextArea } = Input;

const money = (value) =>
	value === null || value === undefined ? '—' : `₹ ${numberWithCommas(Math.round(value))}`;

/**
 * Correct or remove a month-end cost entry that already carries a value.
 *
 * One modal serves both actions because they share the part that matters: the server
 * requires a `reason` on each (422 without one) and writes it to the edit log, since a
 * month-end cost moving after the fact is what a later reconciliation has to explain.
 * The field is therefore mandatory here rather than an optional note, and an
 * antd `Modal.confirm` — which has nowhere to validate one — would not do.
 */
export default function MonthlyCostEditModal({
	open,
	mode,
	record,
	saving,
	isCostPlus,
	onCancel,
	onSubmit,
}) {
	const isDelete = mode === 'delete';
	const [value, setValue] = useState(null);
	const [costToCustomer, setCostToCustomer] = useState(null);
	const [reason, setReason] = useState('');

	// Reset per opening: the modal is mounted once and reused for every row.
	useEffect(() => {
		if (open) {
			setValue(record?.value ?? null);
			setCostToCustomer(record?.cost_to_customer ?? null);
			setReason('');
		}
	}, [open, record]);

	const handleOk = () => {
		const trimmed = reason.trim();
		if (!trimmed) {
			message.error('A reason is required.');
			return;
		}
		if (!isDelete && (value === null || value === undefined || Number.isNaN(Number(value)))) {
			message.error('Enter a value.');
			return;
		}
		if (
			!isDelete &&
			isCostPlus &&
			costToCustomer !== null &&
			costToCustomer !== undefined &&
			Number(costToCustomer) < Number(value)
		) {
			message.error('Cost to Customer cannot be less than Value.');
			return;
		}
		onSubmit(
			isDelete
				? { reason: trimmed }
				: {
						value: Number(value),
						reason: trimmed,
						...(isCostPlus && costToCustomer !== null && costToCustomer !== undefined
							? { cost_to_customer: Number(costToCustomer) }
							: {}),
				  }
		);
	};

	return (
		<Modal
			open={open}
			title={isDelete ? 'Delete Cost Entry' : 'Edit Cost Entry'}
			okText={isDelete ? 'Delete' : 'Save Changes'}
			okButtonProps={{ danger: isDelete, loading: saving }}
			cancelButtonProps={{ disabled: saving }}
			onOk={handleOk}
			onCancel={onCancel}
			destroyOnClose
		>
			<Box mb="16px">
				<Text style={{ fontWeight: 600 }}>{record?.cost_head_name || '—'}</Text>
				<Text style={{ fontSize: 12, color: '#8c8c8c' }}>
					Current value {money(record?.value)}
				</Text>
			</Box>

			{!isDelete && (
				<Box mb="16px">
					<Text style={{ fontSize: 12, fontWeight: 600 }}>
						{isCostPlus ? 'Cost to Company (₹)' : 'Value (₹)'}
					</Text>
					<InputNumber
						min={0}
						style={{ width: '100%' }}
						value={value}
						onChange={setValue}
						placeholder="0"
						aria-label="Value"
					/>
				</Box>
			)}

			{!isDelete && isCostPlus && (
				<Box mb="16px">
					<Text style={{ fontSize: 12, fontWeight: 600 }}>Cost to Customer (₹)</Text>
					<InputNumber
						min={0}
						style={{ width: '100%' }}
						value={costToCustomer}
						onChange={setCostToCustomer}
						placeholder="Same as Value"
						aria-label="Cost to Customer"
					/>
				</Box>
			)}

			<Box>
				<Text style={{ fontSize: 12, fontWeight: 600 }}>Reason *</Text>
				<TextArea
					rows={3}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder={
						isDelete ? 'e.g. Duplicate entry' : 'e.g. Corrected data-entry typo'
					}
					aria-label="Reason"
				/>
				<Text style={{ fontSize: 12, color: '#8c8c8c' }}>
					Recorded on this project&apos;s edit log.
				</Text>
			</Box>
		</Modal>
	);
}

MonthlyCostEditModal.propTypes = {
	open: PropTypes.bool,
	mode: PropTypes.oneOf(['edit', 'delete']),
	record: PropTypes.object,
	saving: PropTypes.bool,
	isCostPlus: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
};

MonthlyCostEditModal.defaultProps = {
	open: false,
	mode: 'edit',
	record: null,
	saving: false,
	isCostPlus: false,
};
